import { parseCricketMatchName } from "../../Games/matchUtils";

/**
 * Demo / lobby-safe prematch-style markets so BULLION shows the intended board per category.
 * Real feed lines (same stable keys where applicable) replace these when merged in roomUtils.
 * Type ids and tab buckets match `bullionCricketMarketGroups.ts`.
 */
export type BullionSyntheticRawMarket = {
    id?: string;
    market_id: string;
    market_type_id: string;
    market_type: string;
    name: string;
    runners: { id: string; name: string; odds: number }[];
};

export function buildCricketSyntheticPrematchRawMarkets(match: { id?: string | number; name?: string }): BullionSyntheticRawMarket[] {
    const mid = String(match.id ?? "fixture");
    const teams = parseCricketMatchName(match.name || "").teams;
    const [tA, tB] = teams.length >= 2 ? [teams[0], teams[1]] : ["Team 1", "Team 2"];
    const p = (suffix: string) => `synth-${mid}-${suffix}`;

    const twoTeam = (suffix: string, typeId: string, mType: string, title: string, teamAOdds = 1.92, teamBOdds = 1.92) =>
        ({
            market_id: p(suffix),
            market_type_id: typeId,
            market_type: mType,
            name: title,
            runners: [
                { id: `${p(suffix)}-a`, name: tA, odds: teamAOdds },
                { id: `${p(suffix)}-b`, name: tB, odds: teamBOdds },
            ],
        }) satisfies BullionSyntheticRawMarket;

    const overUnder = (suffix: string, typeId: string, mType: string, title: string, overOdd = 1.88, underOdd = 1.92) =>
        ({
            market_id: p(suffix),
            market_type_id: typeId,
            market_type: mType,
            name: title,
            runners: [
                { id: `${p(suffix)}-over`, name: "Over", odds: overOdd },
                { id: `${p(suffix)}-under`, name: "Under", odds: underOdd },
            ],
        }) satisfies BullionSyntheticRawMarket;

    const out: BullionSyntheticRawMarket[] = [];

    // —— TEAM ——
    out.push(twoTeam("mw", "1010", "Match Winner", "Match Winner"));
    out.push(twoTeam("toss", "815", "Toss Winner", "Toss Winner", 2, 2));
    out.push(twoTeam("hfo", "50", "Highest Score in First Over", "Highest First Over"));
    out.push(twoTeam("hfs", "55", "Highest Score in First Session", "Highest First Session"));
    out.push(twoTeam("hop", "45", "Highest Opening Partnership", "Highest Opening Partnership"));
    out.push(twoTeam("fours", "30", "Most Fours", "Most Fours"));
    out.push(twoTeam("sixes", "35", "Most Sixes", "Most Sixes"));

    // —— PLAYER (BULLION: batter runs + batter sixes only) ——
    for (let i = 1; i <= 4; i++) {
        const o = 1.86 + i * 0.01;
        const u = 1.96 - i * 0.01;
        out.push(overUnder(`br${i}`, "910", "Pre Match Batsman Runs", `Batter ${i} — runs`, o, u));
    }
    for (let i = 1; i <= 4; i++) {
        const o = 1.88 + i * 0.01;
        const u = 1.94 - i * 0.01;
        out.push(overUnder(`bs${i}`, "914", "Pre Match Batsman Sixes", `Batter ${i} — sixes`, o, u));
    }

    // —— MATCH ——
    out.push(overUnder("mr", "60", "Match Total Runs", "Match Runs", 1.87, 1.95));
    out.push(overUnder("mf", "70", "Match Fours", "Match Fours", 1.9, 1.9));
    out.push(overUnder("ms", "75", "Match Sixes", "Match Sixes", 1.91, 1.89));
    out.push(overUnder("mwk", "85", "Match Wickets", "Match Wickets", 1.9, 1.9));
    out.push(overUnder("mro", "80", "Match Run Outs", "Match Run Outs", 1.92, 1.88));
    out.push(overUnder("mhro", "88", "Match Highest Runs off Over", "Match Highest Runs off Over", 1.89, 1.93));
    out.push(overUnder("mhis", "86", "Match Highest Individual Score", "Match Highest Individual Score", 1.9, 1.9));

    // —— INNINGS ——
    out.push(overUnder("ifour-a", "220", "Innings Fours", `${tA} Total Fours`, 1.9, 1.9));
    out.push(overUnder("ifour-b", "220", "Innings Fours", `${tB} Total Fours`, 1.89, 1.91));
    out.push(overUnder("isix-a", "230", "Innings Sixes", `${tA} Total Sixes`, 1.88, 1.94));
    out.push(overUnder("isix-b", "230", "Innings Sixes", `${tB} Total Sixes`, 1.9, 1.92));
    out.push(overUnder("sra", "510", "Runs in Session", `${tA} Session Runs`, 1.91, 1.91));
    out.push(overUnder("srb", "510", "Runs in Session", `${tB} Session Runs`, 1.9, 1.92));
    out.push(overUnder("fowa", "610", "Fall of Wicket", `${tA} Fall of First Wicket`, 1.89, 1.93));
    out.push(overUnder("fowb", "610", "Fall of Wicket", `${tB} Fall of First Wicket`, 1.88, 1.94));

    return out;
}
